import { useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Empty, Spin, Tag } from "antd";
import {
  CalendarOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Column } from "@antv/g2plot";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isEmpty from "../validations/is-empty";
import apiClient from "../actions/api";
import openNotification from "../components/Notifications/openNotification";
import getDate from "../untils/getDate";
import "../style/chartpage.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

const weekFormat = "YYYY-MM-DD";

const PersonalStatistic = ({ title }) => {
  document.title = title;
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const user = useSelector((state) => state.auth.user);
  const bidsRef = useRef(null);
  const interviewsRef = useRef(null);
  const bidsChart = useRef(null);
  const interviewsChart = useRef(null);
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true).startOf("week"),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true).endOf("week"),
  );

  const destroyChart = (chartRef) => {
    if (!chartRef.current) return;
    try {
      chartRef.current.destroy();
    } catch (error) {
      /* chart was already removed */
    }
    chartRef.current = null;
  };

  useEffect(() => {
    destroyChart(bidsChart);
    if (bids.length && bidsRef.current) {
      bidsChart.current = new Column(bidsRef.current, {
        data: bids,
        xField: "date",
        yField: "count",
        color: "#087b85",
        columnStyle: { radius: [6, 6, 0, 0] },
        meta: { count: { alias: "Bids" }, date: { alias: "Date" } },
      });
      bidsChart.current.render();
    }
    return () => destroyChart(bidsChart);
  }, [bids]);

  useEffect(() => {
    destroyChart(interviewsChart);
    if (interviews.length && interviewsRef.current) {
      interviewsChart.current = new Column(interviewsRef.current, {
        data: interviews,
        xField: "date",
        yField: "count",
        color: "#256bfe",
        columnStyle: { radius: [6, 6, 0, 0] },
        meta: { count: { alias: "Interviews" }, date: { alias: "Date" } },
      });
      interviewsChart.current.render();
    }
    return () => destroyChart(interviewsChart);
  }, [interviews]);

  const customWeekStartEndFormat = (value) =>
    `${dayjs(value).tz(timezoneMode.timezoneMode, true).startOf("week").format(weekFormat)} ~ ${dayjs(value).tz(timezoneMode.timezoneMode, true).endOf("week").format(weekFormat)}`;

  const groupByDate = (items, field) => {
    const counts = new Map();
    items.forEach((item) => {
      const date = getDate(item[field]);
      counts.set(date, (counts.get(date) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const loadStatistics = useCallback(async () => {
    const start = dayjs(searchStartDate)
      .tz(timezoneMode.timezoneMode, true)
      .startOf("day");
    const end = dayjs(searchEndDate)
      .tz(timezoneMode.timezoneMode, true)
      .endOf("day");
    setLoading(true);
    try {
      const { data } = await apiClient.post("/bids/get-personal-statistic", {
        searchStartDate: start.toISOString(),
        searchEndDate: end.toISOString(),
      });
      setBids(groupByDate(data.result || [], "date"));
      setInterviews(groupByDate(data.interviewResult || [], "start_date"));
    } catch (error) {
      setBids([]);
      setInterviews([]);
      const errors = error?.response?.data?.errors;
      if (errors?.length)
        errors.forEach(({ msg }) =>
          openNotification("error", "Statistic Error", msg),
        );
      else
        openNotification(
          "error",
          "Statistic Error",
          "Unable to load your statistic.",
        );
    } finally {
      setLoading(false);
    }
  }, [searchEndDate, searchStartDate, timezoneMode.timezoneMode]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const selectWeek = (date, dateString) => {
    const selected = isEmpty(dateString)
      ? dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true)
      : dayjs(date).tz(timezoneMode.timezoneMode, true);
    setSearchStartDate(selected.startOf("week"));
    setSearchEndDate(selected.endOf("week"));
  };

  const total = (items) => items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="chartPageSection personal-statistic-page">
      <div className="chart-page-heading">
        <div>
          <h2 className="title">
            <span className="chart-title-icon">
              <UserOutlined />
            </span>
            Statistic
          </h2>
          <p>Your weekly bid and interview activity in one view.</p>
        </div>
        <Tag>{user?.name || "My performance"}</Tag>
      </div>

      <div className="per-bidders-statistic-section">
        {loading && (
          <div className="statistics-view-loading" role="status">
            <div className="statistics-view-loading-indicator">
              <Spin size="large" />
              <span>Loading your statistics...</span>
            </div>
          </div>
        )}
        <div className="search-section personal-statistic-filter">
          <div className="filter-title">
            <div>
              <CalendarOutlined /> Reporting period
            </div>
            <span>Select a week to update both sections</span>
          </div>
          <div className="field">
            <div className="label">Week</div>
            <DatePicker
              value={dayjs(searchStartDate)
                .tz(timezoneMode.timezoneMode, true)
                .startOf("week")}
              format={customWeekStartEndFormat}
              picker="week"
              disabled={loading}
              onChange={selectWeek}
            />
          </div>
          <div className="field">
            <div className="label">&nbsp;</div>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loading}
              onClick={loadStatistics}
            >
              Apply
            </Button>
          </div>
        </div>

        <div className="each-chart-view">
          <div className="chart-card-heading">
            <div>
              <h3 className="sub-title">Weekly bids</h3>
              <span>Your bid activity across the selected week</span>
            </div>
            <Tag>{total(bids)} bids</Tag>
          </div>
          <div className="per-bidders-statistic-chart-view">
            {isEmpty(bids) ? (
              <div
                key="personal-bids-empty"
                className={`per-bidders-statistic-chart-empty ${themeMode.themeMode ? "dark-background-color" : ""}`}
              >
                <Empty description="No bids this week" />
              </div>
            ) : (
              <div
                key="personal-bids-chart"
                className={`per-bidders-statistic-chart ${themeMode.themeMode ? "dark-background-color" : ""}`}
                ref={bidsRef}
              />
            )}
          </div>
        </div>

        <div className="each-chart-view">
          <div className="chart-card-heading">
            <div>
              <h3 className="sub-title">Interviews</h3>
              <span>Your interview activity across the selected week</span>
            </div>
            <Tag color="blue">{total(interviews)} interviews</Tag>
          </div>
          <div className="per-bidders-statistic-chart-view">
            {isEmpty(interviews) ? (
              <div
                key="personal-interviews-empty"
                className={`per-bidders-statistic-chart-empty ${themeMode.themeMode ? "dark-background-color" : ""}`}
              >
                <Empty description="No interviews this week" />
              </div>
            ) : (
              <div
                key="personal-interviews-chart"
                className={`per-bidders-statistic-chart ${themeMode.themeMode ? "dark-background-color" : ""}`}
                ref={interviewsRef}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalStatistic;
